import { useEffect, useState } from 'react'
import { DesignResource, ProjectWithOrderedDesignsResource, useDeleteDesignsByDesignMutation, useLazyGetDesignsGroupedByProjectsQuery, useLazyGetDesignsQuery } from '@/api/serverApi'
import { Navbar } from '@/components/header'
import useGroup from '@/hooks/useGroup'
import useSelectView from '@/hooks/useSelectView'
import { useDebouncedCallback } from 'use-debounce'
import { Meta, SearchIcon, TextInput, useInfinitePagination, useSearch, useSort, MinimalSelect, Dropdown, DeleteModal } from '@cfs-fixings/shared'
import ViewPicker from '@/components/browse-views/view-picker/ViewPicker'
import BrowseGroupedDesignsView from '@/components/browse-views/designs/grouped/BrowseGroupedDesignsView'
import BrowseUngroupedDesignsView from '@/components/browse-views/designs/ungrouped/BrowseUngroupedDesignsView'
import CreateButtonHeader from '@/components/header/CreateButtonHeader'
import DuplicateDesignModal from '@/components/modals/designs/DuplicateDesignModal'
import ReassignDesignModal from '@/components/modals/designs/ReassignDesignModal'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import NoDesignsDisplay from '@/components/empty-states/designs/NoDesignsDisplay'

enum SortByOptions {
  alphabetical = 'alphabetical',
  newestFirst = 'newestFirst',
  oldestFirst = 'oldestFirst',
}

enum GroupByOptions {
  allDesigns = 'all',
  byProject = 'project',
}

const textSpec = {
  header: 'All Designs',
  searchPlaceholder: 'Search designs',
  sortByPrefix: 'Sort by',
  groupByPrefix: 'Group by',
  groupOptions: {
    allDesignsLabel: 'All Files',
    byProjectLabel: 'Project',
  },
  sortOptions: {
    alphabetical: 'Alphabetical',
    newestFirst: 'Newest First',
    oldestFirst: 'Oldest First',
  },
  dropdown: {
    duplicateButtonLabel: 'Duplicate',
    reassignButtonLabel: 'Move to Project',
  },
  deleteModal: {
    successMessage: {
      title: 'Design deleted successfully',
      message: 'Design has been removed from the system',
    },
    errorMessage: {
      title: 'Failed to delete design',
      message: 'Design cannot be deleted at this time, please try again later',
    },
    modal: {
      title: 'Delete Design',
      message: 'Are you sure you want to delete this design? This action cannot be undone.',
    },
    button: {
      text: 'Delete',
    },
  },
}

const sortOptions = [
  { value: SortByOptions.alphabetical, label: textSpec.sortOptions.alphabetical },
  { value: SortByOptions.newestFirst, label: textSpec.sortOptions.newestFirst },
  { value: SortByOptions.oldestFirst, label: textSpec.sortOptions.oldestFirst },
]

const groupOptions = [
  { value: GroupByOptions.allDesigns, label: textSpec.groupOptions.allDesignsLabel },
  { value: GroupByOptions.byProject, label: textSpec.groupOptions.byProjectLabel },
]

type DesignDropdownProps = {
  design: DesignResource
  onReset?: () => void
}

const DesignDropdown = ({ design, onReset }: DesignDropdownProps) => {
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false)
  const [isReassignOpen, setIsReassignOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [deleteMutation, { isLoading }] = useDeleteDesignsByDesignMutation()

  const onDelete = () => {
    deleteMutation({ body: {}, design: design.id }).unwrap().then(() => {
      setIsDeleteModalOpen(false)
      toastHelper.success(
        textSpec.deleteModal.successMessage.title,
        textSpec.deleteModal.successMessage.message,
      )

    }).catch(() => {
      toastHelper.error(
        textSpec.deleteModal.errorMessage.title,
        textSpec.deleteModal.errorMessage.message,
      )
    },
    )
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={(e) => e.preventDefault()}>
      <div className="ml-auto">
        <Dropdown>
          <Dropdown.Item onClick={() => setIsDuplicateOpen(true)}>{textSpec.dropdown.duplicateButtonLabel}</Dropdown.Item>
          <Dropdown.Item onClick={() => setIsReassignOpen(true)}>{textSpec.dropdown.reassignButtonLabel}</Dropdown.Item>
          <Dropdown.Separator />
          <Dropdown.Item onClick={() => setIsDeleteModalOpen(true)}>
            {textSpec.deleteModal.button.text}
          </Dropdown.Item>
        </Dropdown>
      </div>
      <DuplicateDesignModal
        design={design}
        isOpen={isDuplicateOpen}
        onClose={() => setIsDuplicateOpen(false)}
        onSuccess={() => {
          setIsDuplicateOpen(false)
          onReset?.()
        }}
      />
      <ReassignDesignModal
        design={design}
        isOpen={isReassignOpen}
        onClose={() => setIsReassignOpen(false)}
        onSuccess={() => {
          setIsReassignOpen(false)
          onReset?.()
        }}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={onDelete}
        title={textSpec.deleteModal.modal.title}
        isLoading={isLoading}
        message={textSpec.deleteModal.modal.message}
      />
    </div>
  )
}

const BrowseDesignsPage = () => {
  const [hasPerformedFirstGroupedLoad, setHasPerformedFirstGroupedLoad] = useState(false)
  const [hasPerformedFirstUngroupedLoad, setHasPerformedUngroupedFirstLoad] = useState(false)

  const { searchTerm, setSearchTerm } = useSearch()
  const { sortBy, setSortBy, sortDirection, setSortDirection } = useSort('updated', 'desc')
  const { viewType, setViewType } = useSelectView({})
  const { groupBy, setGroupBy } = useGroup(GroupByOptions.allDesigns)

  const debouncedSetSearchTerm = useDebouncedCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value),
    500,
  )

  // The RTK query isFetching has proven unreliable in this case
  const [isFetching, setIsFetching] = useState(false)

  const [fetchDesignsGrouped] = useLazyGetDesignsGroupedByProjectsQuery({})
  const [fetchDesignsUngrouped] = useLazyGetDesignsQuery({})

  const { data: groupedData, ref: groupedRef, reset: groupedReset } = useInfinitePagination<ProjectWithOrderedDesignsResource>({
    loadData: (page) => {
      setIsFetching(true)

      return fetchDesignsGrouped({
        search: searchTerm,
        sortDirection: sortDirection,
        sortBy: sortBy,
        page: page,
        perPage: 45,
      }).then(res => {
        setHasPerformedFirstGroupedLoad(true)
        setIsFetching(true)

        return res.data
      })
    },
    isActive: groupBy === GroupByOptions.byProject,
  })

  const { data: ungroupedData, ref: ungroupedRef, reset: ungroupedReset } = useInfinitePagination<DesignResource>({
    loadData: (page) => {
      setIsFetching(true)

      return fetchDesignsUngrouped({
        search: searchTerm,
        sortDirection: sortDirection,
        sortBy: sortBy,
        page: page,
        perPage: 45,
      }).then(res => {
        setHasPerformedUngroupedFirstLoad(true)
        setIsFetching(false)

        return res.data
      })
    },
    isActive: groupBy === GroupByOptions.allDesigns,
  })

  useEffect(() => {
    setIsFetching(true)

    // Any time the search or sort is changed we want to clear data and refetch
    groupedReset()
    ungroupedReset()
  }, [groupedReset, ungroupedReset, searchTerm, sortBy, sortDirection, groupBy])

  return <>
    <Meta title="Designs" />

    <Navbar
      topbar={
        <CreateButtonHeader>
          <h1 className="font-semibold text-[24px] text-gray-900">{textSpec.header}</h1>
        </CreateButtonHeader>
      }
    >
      <div className="flex flex-col w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 pb-8 gap-y-2 px-2.5">
          <TextInput
            defaultValue={searchTerm}
            prefixIcon={
              <SearchIcon className="w-[16px] h-[16px] text-gray-600" />
            }
            placeholder={textSpec.searchPlaceholder}
            onChange={debouncedSetSearchTerm}
          />
          <div className="flex md:flex-row flex-col md:justify-end md:items-center items-start justify-center gap-y-3">
            <div className="flex items-center justify-end gap-2 pr-[18px] lg:pr-[38px]">
              <p className="text-sm leading-[19.6px]">{textSpec.sortByPrefix}</p>
              <MinimalSelect
                defaultValue={
                  sortBy === 'name'
                    ? SortByOptions.alphabetical
                    : sortBy === 'updated' && sortDirection === 'desc'
                      ? SortByOptions.newestFirst
                      : SortByOptions.oldestFirst
                }
                options={sortOptions}
                onChange={(option) => {
                  if (option.value === SortByOptions.alphabetical) {
                    setSortBy('name')
                    setSortDirection('asc')
                  } else if (option.value === SortByOptions.newestFirst) {
                    setSortBy('updated')
                    setSortDirection('desc')
                  } else {
                    setSortBy('updated')
                    setSortDirection('asc')
                  }
                }}
              />
            </div>
            <div className="flex items-center justify-end gap-2 pr-[18px] lg:pr-[38px]">
              <p className="text-sm leading-[19.6px]">{textSpec.groupByPrefix}</p>
              <MinimalSelect
                defaultValue={groupBy}
                options={groupOptions}
                onChange={(option) => {
                  setHasPerformedFirstGroupedLoad(false)
                  setHasPerformedUngroupedFirstLoad(false)
                  setGroupBy?.(option.value)
                }}
              />
            </div>
            <div className="self-end">
              <ViewPicker
                selectedOption={viewType}
                onOptionSelected={(viewType) => setViewType(viewType)}
              />
            </div>
          </div>
        </div>
        {
          (groupBy === GroupByOptions.allDesigns && ungroupedData?.length === 0 && !isFetching && hasPerformedFirstUngroupedLoad)
          || (groupBy === GroupByOptions.byProject && groupedData?.length === 0 && !isFetching && hasPerformedFirstGroupedLoad)
            ? (
              <NoDesignsDisplay />
            )
            : null
        }
        {
          groupBy === GroupByOptions.allDesigns
            ? (
              <BrowseUngroupedDesignsView
                viewType={viewType}
                designs={ungroupedData}
                footerElements={[
                  <div key="project-pagination-ref" ref={ungroupedRef} />,
                ]}
                isLoading={isFetching}
                dropdown={(design: DesignResource) => (
                  <DesignDropdown
                    design={design}
                    onReset={() => {
                      // This is nasty, but necessary. The designs data needs to be manually reset
                      // as it's a lazy query, but if this happens at the same time as the cache invalidation
                      // it returns the cached data. This artificial delay addresses that.
                      // Related issue: https://github.com/reduxjs/redux-toolkit/issues/2802
                      setTimeout(() => {
                        groupedReset()
                        ungroupedReset()
                      }, 1000)
                    }}
                  />
                )}
              />
            )
            : (
              <BrowseGroupedDesignsView
                viewType={viewType}
                projects={groupedData}
                footerElements={[
                  <div key="project-pagination-ref" ref={groupedRef} />,
                ]}
                isLoading={isFetching}
                dropdown={(design: DesignResource) => (
                  <DesignDropdown
                    design={design}
                    onReset={() => {
                      // This is nasty, but necessary. The designs data needs to be manually reset
                      // as it's a lazy query, but if this happens at the same time as the cache invalidation
                      // it returns the cached data. This artificial delay addresses that.
                      // Related issue: https://github.com/reduxjs/redux-toolkit/issues/2802
                      setTimeout(() => {
                        groupedReset()
                        ungroupedReset()
                      }, 1000)
                    }}
                  />
                )}
              />
            )
        }
      </div>
    </Navbar>
  </>
}

export default BrowseDesignsPage
